// Session 36: the onboarding screen.
//
// Leena: "We need it to say, here's your onboarding screen. You selected these
// three items. Walk through and get yourself onboarded."
//
// Which sections appear comes from what they bought — v_portal_onboarding_me
// returns the plans and their groups — so somebody who bought only GMB is not
// asked about blog tone. Each section saves on its own through
// portal_save_onboarding, so a long form survives a closed tab.
//
// Answers that have a real home in the pack are promoted by that function as
// they are given: the website, the GA4 property, the Search Console property,
// what they do, their service area, and the whole voice block the drafter
// reads. There is one copy of each, not a form's idea and a pipeline's idea.
//
// Session 55 (Wave 16): the same screen is "here is what we have". A client
// Leena created by hand arrives with the pack already filled from their
// website; every section shows those values and the button reads "Looks
// right, save it". Four sections were added for the things the drafter leans
// on and nobody had been asked for: the author, the pages we may link to,
// proof, and where images come from. An active client reaches the screen
// again from "Your details" in the rail, so Kiva and Build A Better Website
// can fill their gaps.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';   // Session 68b: tell an agency login whose screen this is
import { SaveStatus, useSaveStatus } from '../components/SaveStatus.jsx';
// Wave 13 (session 46): the newsletter section, shared with the admin settings.
import NewsletterSetup, { newsletterComplete, newsletterFromPack, newsletterPayload } from '../components/NewsletterSetup.jsx';
import BrandSetup, { ImagesSetup, brandFromPack, brandPayload, imagesFromPack } from '../components/HeroLooks.jsx';   // Session 63/64

/* --------------------------------------------------------------- the copy */

// Leena's words, lightly set out. The access instructions are deliberately
// instructions rather than a request button: she chose that, and it means
// nothing of ours has to hold a Google credential.
const ACCESS_STEPS = {
  ga4: [
    'In Google Analytics, open Admin (the cog, bottom left).',
    'Under Property, choose Property access management.',
    'Click +, then Add users, and enter ops@digitallotusmarketing.com.',
    'Give it Viewer, leave "Notify by email" ticked, and click Add.',
    'Your Property ID is the number under Admin → Property details. Paste it above.',
  ],
  gsc: [
    'In Google Search Console, pick your site, then Settings → Users and permissions.',
    'Click Add user and enter ops@digitallotusmarketing.com.',
    'Set permission to Full, then Add.',
    'Copy the property exactly as Search Console shows it (including sc-domain: if it is there).',
  ],
  gmb: [
    'Open your Google Business Profile and go to Businesses → the business → Managers.',
    'Click Add, enter ops@digitallotusmarketing.com and choose Manager.',
    'Send the invitation. We accept it and you keep ownership — Manager never means we own the listing.',
  ],
};

const TONE_MODES = [
  ['seo', 'SEO first',
   'Written for the keywords people actually search. The most likely to be found, and the most obviously structured for it.'],
  ['mix', 'A mix',
   'SEO structure, but written to sound like you. What most people choose.'],
  ['brand', 'Your voice first',
   'Written mostly for how you want to come across. Still sensible about search, but it comes second.'],
];

const TONE_WORDS = [
  'Warm', 'Plain-spoken', 'Expert', 'Playful', 'Calm', 'Direct',
  'Reassuring', 'Premium', 'Practical', 'Down to earth', 'Precise', 'Enthusiastic',
];

// Leena, Session 37: "two ways you can work through this: one is auto-approve
// ... there will always be a 5-day deadline by which you need to give us the
// changes. If you do not respond in those 5 days, that plan will get
// auto-approved so we can continue to move forward." And the other: nothing
// goes forward without you.
const APPROVAL_MODES = [
  ['client_deadline', 'Keep things moving',
   'We email you every plan, every question and every draft, and you have five days to come back. '
   + 'If we hear nothing in five days we take it as a yes and carry on, so your blogs never stall. '
   // Session 55 Part B: a fast-start client's first plan and first questions run on a 2-day clock
   + '(When we fast-start your blog, the very first plan and the first set of questions have two days, so your first post can go out on time.) '
   + 'You can still change anything afterwards.'],
  ['client', 'Nothing without me',
   'Nothing is written, scheduled or published until you have said yes to it. '
   + 'The safest choice, and the one that waits for you — if you are busy for a fortnight, so are your blogs.'],
];

const PRICE_POINTS = [
  ['budget', 'Keenest price in the area'],
  ['middle', 'Mid-range, good value'],
  ['premium', 'Premium — people pay more for the quality'],
  ['bespoke', 'Bespoke, priced per job'],
];

// Session 55: where the pictures come from. Wave 19 builds the making; this
// is the choice, so a plan can be made knowing it.
// What happens next, per service, in her words: "This is how it works... you're
// going to get approval for your next 3 months of blogs. Note that's not final
// and that you will be able to change them in the future."
const WHAT_NEXT = {
  blogs: {
    title: 'What happens next with your blogs',
    lines: [
      'We research your market and put together a plan of posts for the next three months.',
      'You will get that plan to approve. It is not final — you can change the topics then, and at any point afterwards.',
      'Once you approve it, we start writing. Before each post we ask you a couple of short questions so it is specific to you.',
      'You read every post and approve it before it goes anywhere.',
    ],
    closing: 'Nothing more for you to do. Wait for the strategy to land and have a look around your dashboard in the meantime.',
  },
  seo: {
    title: 'What happens next with your on-page SEO',
    lines: [
      'We review how your site reads to Google and to the AI crawlers.',
      'You will get a checklist of fixes in priority order, in plain English, with the reason for each one.',
      'You make the changes on your site; we talk you through anything that is not obvious.',
      'Reviewed again every quarter.',
    ],
    closing: 'Nothing more for you to do until the first checklist arrives.',
  },
  gmb: {
    title: 'What happens next with your Google Business Profile',
    lines: [
      'Once you have added us as a Manager we go through the whole profile.',
      'You will get the changes that move you up locally, and why each one matters.',
      'You stay the owner of the listing throughout.',
    ],
    closing: 'Nothing more for you to do until the review arrives.',
  },
  courses: {
    title: 'Your courses',
    lines: ['Your courses are already open — there is nothing to set up.'],
    closing: 'Have a look whenever you like.',
  },
  // Wave 13 (session 46): one newsletter per post, sent by the client themselves.
  // Wave 19 (session 59): the same words as the welcome email (Leena 2026-10-19).
  email: {
    title: 'What happens next with your newsletter',
    lines: [
      'Every blog post also becomes a newsletter in your branding. You will find it under Newsletters beside each post: read it there, download it as email HTML, or download a package for Mailchimp and send it from your own account.',
      'We never send it for you, and you can change any of the wording in your email tool before it goes.',
      'You pick your template, colours, logo and call to action during set-up, and you can change them any time under Newsletters.',
    ],
    closing: 'Nothing more for you to do once you have picked your template below.',
  },
};

/* ------------------------------------------------------------- the pieces */

function Steps({ steps }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="link-btn onb-steps-toggle" aria-expanded={open}
              onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide the steps' : 'How do I give you access?'}
      </button>
      {open && <ol className="onb-steps">{steps.map((t, i) => <li key={i}>{t}</li>)}</ol>}
    </>
  );
}

// Session 55: `prefilled` turns the first save into "Looks right, save it".
// Saving unchanged values is the confirmation; there is no separate flag,
// because a confirmed section and a saved section are the same thing to the
// pipeline: values it may use.
function Section({ id, title, lede, saved, children, onSave, canSave = true, prefilled = false, optional = false }) {
  const save = useSaveStatus();
  const done = !!saved?.saved_at;
  const label = save.status === 'saving' ? 'Saving…'
    : done ? 'Save changes'
    : prefilled ? 'Looks right, save it'
    : 'Save and carry on';
  return (
    <section id={id} className={`review-section onb-section${done ? ' onb-section-done' : ''}`}>
      <div className="onb-section-head">
        <h2>
          {title}
          {done && <span className="chip course-chip">saved</span>}
          {!done && optional && <span className="chip chip-run-muted">optional</span>}
        </h2>
      </div>
      {lede && <p className="hint">{lede}</p>}
      {children}
      <div className="review-actions" style={{ justifyContent: 'flex-start' }}>
        <button type="button" className="btn btn-primary"
                disabled={save.status === 'saving' || !canSave}
                onClick={async () => {
                  save.start();
                  const err = await onSave();
                  if (err) save.fail(err); else save.ok();
                }}>
          {label}
        </button>
        <SaveStatus status={save.status} />
      </div>
    </section>
  );
}

// Session 55: a list of rows with a text and an optional link (proof).
function ProofRows({ rows, setRows }) {
  const update = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="row-list">
      {rows.map((r, i) => (
        <div className="row-item" key={i}>
          <input placeholder="What it is: 4.9 stars on Google, 20 years in business, an award" value={r.text || ''}
                 maxLength={400} onChange={(e) => update(i, 'text', e.target.value)} />
          <input type="url" placeholder="Where it can be seen (optional)" value={r.url || ''}
                 maxLength={400} onChange={(e) => update(i, 'url', e.target.value)} />
          <button type="button" className="link-btn" aria-label="Remove row"
                  onClick={() => setRows(rows.filter((_, j) => j !== i))}>Remove</button>
        </div>
      ))}
      <button type="button" className="link-btn" onClick={() => setRows([...rows, { text: '', url: '' }])}>
        + Add one
      </button>
    </div>
  );
}

/* ------------------------------------------------------------- the page */

export default function Welcome() {
  // Session 68b: this page reads v_portal_onboarding_me, which is the
  // signed-in USER's own client. For an agency login that is Digital Lotus,
  // whatever the client switcher says, so Leena opening the demo's welcome
  // email with her agency login saw Digital Lotus's details under the demo's
  // name. Say so, and say how to see it as the client.
  const { isAgency, activeClient } = useAuth();
  const agencyNote = isAgency ? (
    <div className="banner banner-info">
      You are signed in as the agency, so this is Digital Lotus's own Welcome screen{activeClient && activeClient.client_id !== 'digitallotus' ? `, not ${activeClient.name}'s` : ''}.
      {' '}A client's answers are under Admin, Onboarding. To see this screen the way a client does, sign in with that client's sign-in address in a private window (for a demo, its plus address).
    </div>
  ) : null;
  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);
  const [biz, setBiz] = useState({
    website: '', ga4_property_id: '', gsc_property: '', gmb_invited: '',
    what_you_do: '', service_area: '',
  });
  const [blogs, setBlogs] = useState({
    tone_mode: 'mix', tone_words: [], appear_as: '', ideal_client: '',
    price_point: '', avoid: '',
  });
  const [approval, setApproval] = useState({ mode: 'client_deadline', deadline_days: '5' });
  // Session 55: the four new sections
  const [author, setAuthor] = useState({ name: '', title: '', bio: '', url: '' });
  const [links, setLinks] = useState({ allowlist: [], extra: '' });
  const [proof, setProof] = useState([]);
  const [images, setImages] = useState({ source: '', look: 'editorial', notes: '' });   // Session 63/64
  const [brand, setBrand] = useState(brandFromPack(null, null));                       // Session 64
  // Wave 13 (session 46): the newsletter answers. Prefilled from pack.newsletter
  // (the promoted copy), which is what the droplet actually builds from.
  const [newsletter, setNewsletter] = useState(() => newsletterFromPack(null, null));
  const [finishing, setFinishing] = useState(false);
  const [banner, setBanner] = useState(null);
  // Session 49 (Wave 14, pack 12): back from paying for a service, the page
  // opens on that service's section even for a client who finished onboarding
  // long ago. Read once from the URL; the section ids are the group codes'
  // sections (email -> newsletter).
  const [added] = useState(() => {
    const g = new URLSearchParams(window.location.search).get('added');
    return ({ email: 'newsletter', blogs: 'blogs' })[g] || null;
  });

  // Session 55 (review 3): the form fields are filled from the server once,
  // on the first load. Later loads (after each save) refresh `me` only, so a
  // section being edited is never reset by saving a different one.
  const filledRef = useRef(false);

  async function load() {
    const { data, error: e } = await supabase
      .from('v_portal_onboarding_me').select('*').maybeSingle();
    if (e) { setError(e.message); return; }
    setMe(data || false);
    if (!data || filledRef.current) {
      // the newsletter section keeps its Wave 13 behaviour: always from the pack
      if (data) {
        setNewsletter((n) => ({
          ...newsletterFromPack(data?.newsletter, { ...(data?.business || {}), website: data?.website || '' }),
          ...(n.logo_preview_url && !data?.newsletter?.logo_path ? { logo_path: n.logo_path, logo_preview_url: n.logo_preview_url } : {}),
        }));
      }
      return;
    }
    filledRef.current = true;
    const sections = data?.sections || {};
    const business = data?.business || {};
    const voice = data?.voice || {};
    const an = data?.analytics || {};
    // Session 55: while a client is onboarding, a section they saved shows
    // their answers and an unsaved one shows what the pack already has (from
    // Add client), so they see what we know and confirm or correct it. For an
    // active client coming back (review 4) the pack wins: it may have been
    // curated in Edit client since, and re-saving must not push old answers
    // back over that.
    const isReturning = data.status !== 'onboarding';
    const saved = (name) => (isReturning ? {} : (sections[name] || {}));
    // description_shown travels with the save: the function updates the
    // drafter's description when the client edits the text they were shown
    // (review 3, second pass), and leaves a curated one they never saw alone.
    const shown = business.description || business.what_you_do || '';
    setBiz((b) => ({
      ...b,
      website: data?.website || business.website || '',
      what_you_do: shown,
      service_area: business.service_area || '',
      ga4_property_id: an.ga4_property_id || '',
      gsc_property: an.gsc_property || an.gsc_site_url || '',
      ...saved('business'),
      description_shown: shown,
    }));
    const sb = saved('blogs');
    setBlogs((b) => ({
      ...b,
      tone_mode: voice.tone_mode || b.tone_mode,
      tone_words: Array.isArray(voice.tone_words) ? voice.tone_words : [],
      appear_as: voice.how_you_want_to_appear || '',
      ideal_client: voice.ideal_client || '',
      price_point: voice.price_point || '',
      avoid: voice.avoid || '',
      ...sb,
      ...(sb.tone_words !== undefined ? { tone_words: Array.isArray(sb.tone_words) ? sb.tone_words : [] } : {}),
    }));
    // review 1: the approval section shows the mode that is actually in force
    // (the view's effective mode, so a client whose pack has no approval block
    // reads as 'client', which is what _s13_mode gives them), never a default.
    // Only a fresh Stripe sign-up, who has not been given a mode yet, keeps
    // the Session 37 default. (agency_only clients do not see the section.)
    const ap = data?.approval || {};
    const hasDetails = isReturning || !!data?.prefilled || ap.set === true;
    setApproval((a) => ({
      ...a,
      ...(hasDetails && ap.mode ? { mode: ap.mode } : {}),
      ...(hasDetails && ap.deadline_days ? { deadline_days: String(ap.deadline_days) } : {}),
      ...saved('approval'),
    }));
    const by = data?.byline || {};
    setAuthor({ name: by.name || '', title: by.title || '', bio: by.bio || '', url: by.url || '', ...saved('author') });
    setLinks({ allowlist: Array.isArray(data?.allowlist) ? data.allowlist : [], extra: '' });
    setProof(Array.isArray(data?.market?.proof) ? data.market.proof.map((p) => ({ text: p.text || '', url: p.url || '' })) : []);
    setImages({ ...imagesFromPack(data?.images), source: data?.images?.source ? imagesFromPack(data?.images).source : '' });
    // Session 64: the brand, from pack.brand or (for a client set up before
    // it existed) the newsletter colours; a logo just uploaded is kept
    setBrand((b) => ({ ...brandFromPack(data?.brand, data?.newsletter),
                       ...(b.logo_preview_url && !data?.brand?.logo_path ? { logo_path: b.logo_path, logo_preview_url: b.logo_preview_url } : {}) }));
    setNewsletter((n) => ({
      ...newsletterFromPack(data?.newsletter, { ...(data?.business || {}), website: data?.website || sections.business?.website || '' }),
      // keep a logo just uploaded but not yet saved
      ...(n.logo_preview_url && !data?.newsletter?.logo_path ? { logo_path: n.logo_path, logo_preview_url: n.logo_preview_url } : {}),
    }));
    if (added && data?.completed_at) {
      // Session 49: they just paid for something that needs answers; the
      // "all set" screen would hide the section. Local only, like "Change one
      // of my answers".
      setMe({ ...data, completed_at: null });
      setTimeout(() => document.getElementById(added)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }
  useEffect(() => { load(); }, []);

  const groups = useMemo(() => {
    const seen = [];
    for (const b of me?.bought || []) {
      if (!seen.find((g) => g.group_code === b.group_code)) {
        seen.push({ group_code: b.group_code, group_name: b.group_name, plans: [] });
      }
      seen.find((g) => g.group_code === b.group_code).plans.push(b.plan_name);
    }
    // Session 55: a client switched on by hand has entitlements and no plan
    // codes (Kiva, Build A Better Website, Landry). What is switched on counts.
    const ents = Array.isArray(me?.entitlements) ? me.entitlements : [];
    const add = (code, name) => { if (!seen.find((g) => g.group_code === code)) seen.push({ group_code: code, group_name: name, plans: [] }); };
    if (ents.includes('seo_blogs')) add('blogs', 'Blog content');
    if (ents.includes('gmb_audit')) add('gmb', 'Google Business Profile');
    if (ents.includes('seo_recommendations')) add('seo', 'On-page SEO');
    return seen;
  }, [me]);

  const has = (code) => groups.some((g) => g.group_code === code);
  // Wave 13: the newsletter section shows for a client who bought it OR one
  // Leena switched on (decision 15: nobody gets it automatically; she switches
  // it on per client, and a switched-on client still has to pick a template).
  const hasNewsletter = has('email') || !!me?.newsletter_on;
  // Session 55: details already on file (Add client, or an active client
  // coming back through "Your details")
  const prefilled = !!me?.prefilled || (me && me.status !== 'onboarding');
  const returning = !!me && me.status !== 'onboarding';
  // review 1: an agency_only client is run by Leena; the approval choice is
  // not theirs to make and the section is not shown (the RPC refuses too).
  const agencyOnly = me?.approval?.mode === 'agency_only';

  async function saveSection(section, answers) {
    const { error: e } = await supabase.rpc('portal_save_onboarding', {
      p_section: section, p_answers: answers,
    });
    if (e) return e.message;
    await load();
    return null;
  }

  async function finish() {
    setFinishing(true); setBanner(null);
    const { error: e } = await supabase.rpc('portal_complete_onboarding');
    setFinishing(false);
    if (e) { setBanner({ tone: 'err', text: e.message }); return; }
    await load();
  }

  if (error) return <div className="empty"><h2>Couldn't load your setup</h2><p>{error}</p></div>;
  if (me === null) return <p className="lede">Loading…</p>;
  if (me === false) return <div className="empty"><h2>Nothing to set up</h2></div>;

  const sections = me.sections || {};
  const businessDone = !!sections.business?.saved_at;
  const blogsDone = !has('blogs') || !!sections.blogs?.saved_at;
  const approvalDone = !has('blogs') || agencyOnly || !!sections.approval?.saved_at;
  // Session 55: the author and where images come from are needed before a
  // post can be written; links and proof help but can wait.
  const authorDone = !has('blogs') || !!sections.author?.saved_at;
  const imagesDone = !has('blogs') || !!sections.images?.saved_at;
  // Wave 13: a newsletter cannot be built without a template, so the section
  // has to be saved before they are done (the call to action has defaults).
  const newsletterDone = !hasNewsletter || !!me.newsletter?.template;
  const canFinish = businessDone && blogsDone && approvalDone && authorDone && imagesDone && newsletterDone;
  const nextGroups = hasNewsletter && !has('email')
    ? [...groups, { group_code: 'email', group_name: 'Newsletter', plans: [] }] : groups;
  // business, then for blogs: how they read, approvals (unless agency only),
  // author, links, proof, images; then the newsletter
  const sectionCount = 1 + (has('blogs') ? (agencyOnly ? 5 : 6) : 0) + (hasNewsletter ? 1 : 0);

  if (me.completed_at) {
    return (
      <>
        <h1>{returning ? 'Your details' : "You're all set"}</h1>
        {agencyNote}
        <p className="lede">
          {returning
            ? 'Everything we know about your business, your voice and your author. Change anything that is out of date and we use the new version from the next post on.'
            : 'Thank you — that is everything we need from you for now. We are putting your plan together and will be in touch shortly.'}
        </p>
        {/* Wave 13: switched on after they finished, and nothing picked yet */}
        {hasNewsletter && !me.newsletter?.template && (
          <div className="banner banner-info">
            Your newsletter is ready to set up: pick a template and we will make one
            for every post that goes out.{' '}
            <button type="button" className="link-btn" onClick={() => setMe({ ...me, completed_at: null })}>
              Set up my newsletter
            </button>
          </div>
        )}
        {!returning && nextGroups.map((g) => {
          const next = WHAT_NEXT[g.group_code];
          if (!next) return null;
          return (
            <section key={g.group_code} className="review-section">
              <h2>{next.title}</h2>
              <ul>{next.lines.map((l, i) => <li key={i}>{l}</li>)}</ul>
              {next.closing && <p className="hint">{next.closing}</p>}
            </section>
          );
        })}
        {!returning && (
          <p className="hint">
            Nothing more for you to do. <Link to="/">Have a look around your dashboard</Link>{' '}
            in the meantime — you can come back here and change any of your answers whenever you like.
          </p>
        )}
        <div className="review-actions" style={{ justifyContent: 'flex-start' }}>
          <button type="button" className="btn btn-primary"
                  onClick={() => setMe({ ...me, completed_at: null })}>
            {returning ? 'Open my details' : 'Change one of my answers'}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <h1>{returning ? 'Your details' : prefilled ? "Welcome — check what we have" : "Welcome — let's get you set up"}</h1>
      {agencyNote}
      <p className="lede">
        {prefilled ? (
          <>
            We have filled in what we know about your business.
            Go through each section below: if it looks right, save it; if not, change it first.
            {' '}{sectionCount === 1 ? 'One short section' : `${['', '', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight'][sectionCount] || sectionCount} short sections`}, about ten minutes,
            and you can stop and come back — everything saves as you go.
          </>
        ) : (
          <>
            You signed up for {nextGroups.map((g) => g.group_name).join(', ') || 'our services'}.
            There {sectionCount === 1 ? 'is one short section' : `are ${['', '', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight'][sectionCount] || sectionCount} short sections`} below. It takes
            about {has('blogs') || hasNewsletter ? 'ten' : 'five'} minutes, and you can stop and come back —
            everything saves as you go.
          </>
        )}
      </p>

      {added === 'newsletter' && (
        <div className={`banner ${me.newsletter_on ? 'banner-ok' : 'banner-info'}`}>
          {me.newsletter_on
            ? 'Your newsletter is on. Pick how it should look below and save, and your last two posts get one straight away.'
            : 'Thank you. Stripe is confirming your payment; this page updates once your newsletter is on, usually within a minute. Refresh if it has not.'}
        </div>
      )}

      {me.subscription_status === 'trialing' && me.trial_end && (
        <div className="banner banner-info">
          You are in your free trial. Nothing is charged until{' '}
          {new Date(me.trial_end).toLocaleDateString(undefined,
            { month: 'long', day: 'numeric', year: 'numeric' })}.
        </div>
      )}

      {banner && <div className={`banner banner-${banner.tone === 'ok' ? 'ok' : 'err'}`}>{banner.text}</div>}

      {/* ------------------------------------------------ 1. the business */}
      <Section
        id="business"
        title="About your business"
        lede="The basics, and the accounts we need to see so we can measure whether any of this is working."
        saved={sections.business}
        canSave={!!biz.website.trim()}
        prefilled={prefilled}
        onSave={() => saveSection('business', biz)}
      >
        <label className="field">
          <span className="field-label">Your website</span>
          <input type="text" value={biz.website} maxLength={400}
                 placeholder="yourbusiness.com"
                 onChange={(e) => setBiz({ ...biz, website: e.target.value })} />
        </label>
        <label className="field">
          <span className="field-label">What do you do, in a sentence or two?</span>
          <textarea rows={3} value={biz.what_you_do} maxLength={2000}
                    onChange={(e) => setBiz({ ...biz, what_you_do: e.target.value })} />
          {returning && <span className="field-hint">This is the description our writer works from. Change it and the next post uses the new version.</span>}
        </label>
        <label className="field">
          <span className="field-label">Where do you serve?</span>
          <input type="text" value={biz.service_area} maxLength={400}
                 placeholder="Cities or regions, or just: national"
                 onChange={(e) => setBiz({ ...biz, service_area: e.target.value })} />
        </label>

        <h3 className="onb-sub">Google Analytics</h3>
        <label className="field">
          <span className="field-label">Your GA4 Property ID, if you have one</span>
          <input type="text" value={biz.ga4_property_id} maxLength={40}
                 placeholder="e.g. 123456789 — leave blank if you are not sure"
                 onChange={(e) => setBiz({ ...biz, ga4_property_id: e.target.value })} />
        </label>
        <p className="hint">
          We only ever read it. If you do not have Analytics, leave this blank and
          we will set it up for you.
        </p>
        <Steps steps={ACCESS_STEPS.ga4} />

        <h3 className="onb-sub">Google Search Console</h3>
        <label className="field">
          <span className="field-label">Your Search Console property, if you have one</span>
          <input type="text" value={biz.gsc_property} maxLength={400}
                 placeholder="e.g. sc-domain:yourbusiness.com"
                 onChange={(e) => setBiz({ ...biz, gsc_property: e.target.value })} />
        </label>
        <p className="hint">
          This is what tells us which searches you are actually showing up for.
          Leave it blank if you do not have it and we will help you set it up.
        </p>
        <Steps steps={ACCESS_STEPS.gsc} />

        {has('gmb') && (
          <>
            <h3 className="onb-sub">Google Business Profile</h3>
            <label className="field">
              <span className="field-label">Have you added us as a Manager yet?</span>
              <select className="course-input" value={biz.gmb_invited}
                      onChange={(e) => setBiz({ ...biz, gmb_invited: e.target.value })}>
                <option value="">Choose…</option>
                <option value="yes">Yes, invitation sent</option>
                <option value="not_yet">Not yet</option>
                <option value="need_help">I need a hand with this</option>
              </select>
            </label>
            <p className="hint">
              You stay the owner of your listing. Manager lets us make the changes
              and nothing more.
            </p>
            <Steps steps={ACCESS_STEPS.gmb} />
          </>
        )}
      </Section>

      {/* ---------------------------------------------------- 2. the blogs */}
      {has('blogs') && (
        <Section
          id="blogs"
          title="How your blogs should read"
          saved={sections.blogs}
          prefilled={prefilled}
          onSave={() => saveSection('blogs', blogs)}
        >
          <p className="hint">
            We recommend SEO-focused posts, and it is worth knowing what that means:
            they are written around the keywords people search for, which shapes the
            length and some of the structure. They are still written with your input
            and you approve every one. Where would you like us to sit?
          </p>

          <div className="onb-choices">
            {TONE_MODES.map(([value, name, why]) => (
              <label key={value}
                     className={`onb-choice${blogs.tone_mode === value ? ' onb-choice-on' : ''}`}>
                <input type="radio" name="tone_mode" value={value}
                       checked={blogs.tone_mode === value}
                       onChange={() => setBlogs({ ...blogs, tone_mode: value })} />
                <span>
                  <span className="onb-choice-name">{name}</span>
                  <span className="onb-choice-why">{why}</span>
                </span>
              </label>
            ))}
          </div>

          <h3 className="onb-sub">How should it sound?</h3>
          <p className="hint">Pick as many as fit. Three or four is plenty.</p>
          <div className="onb-words">
            {TONE_WORDS.map((w) => {
              const on = blogs.tone_words.includes(w);
              return (
                <button key={w} type="button"
                        className={`onb-word${on ? ' onb-word-on' : ''}`}
                        aria-pressed={on}
                        onClick={() => setBlogs({
                          ...blogs,
                          tone_words: on ? blogs.tone_words.filter((x) => x !== w)
                                         : [...blogs.tone_words, w],
                        })}>
                  {w}
                </button>
              );
            })}
          </div>

          <label className="field">
            <span className="field-label">How do you want to come across?</span>
            <textarea rows={2} value={blogs.appear_as} maxLength={2000}
                      placeholder="The one people call when it matters. The friendly local option. The expert."
                      onChange={(e) => setBlogs({ ...blogs, appear_as: e.target.value })} />
          </label>

          <label className="field">
            <span className="field-label">Who do you most want to reach?</span>
            <textarea rows={2} value={blogs.ideal_client} maxLength={2000}
                      placeholder="The customers you would take more of, and the ones you would rather not."
                      onChange={(e) => setBlogs({ ...blogs, ideal_client: e.target.value })} />
          </label>

          <label className="field">
            <span className="field-label">Where do you sit on price?</span>
            <select className="course-input" value={blogs.price_point}
                    onChange={(e) => setBlogs({ ...blogs, price_point: e.target.value })}>
              <option value="">Choose…</option>
              {PRICE_POINTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <p className="hint">
            This changes how we write, not what we charge. Premium and keenest-price
            are written quite differently.
          </p>

          <label className="field">
            <span className="field-label">Anything we should never write about?</span>
            <textarea rows={2} value={blogs.avoid} maxLength={2000}
                      placeholder="Topics, claims, competitors, anything that would make you wince."
                      onChange={(e) => setBlogs({ ...blogs, avoid: e.target.value })} />
          </label>
        </Section>
      )}

      {/* -------------------------------------------- 2b. how approvals work */}
      {has('blogs') && !agencyOnly && (
        <Section
          id="approval"
          title="How much do you want to be asked?"
          lede="Either way you see everything and can change anything. This is only about what happens when you are busy."
          saved={sections.approval}
          prefilled={prefilled}
          onSave={() => saveSection('approval', approval)}
        >
          <div className="onb-choices">
            {APPROVAL_MODES.map(([value, name, why]) => (
              <label key={value}
                     className={`onb-choice${approval.mode === value ? ' onb-choice-on' : ''}`}>
                <input type="radio" name="approval_mode" value={value}
                       checked={approval.mode === value}
                       onChange={() => setApproval({ ...approval, mode: value })} />
                <span>
                  <span className="onb-choice-name">{name}</span>
                  <span className="onb-choice-why">{why}</span>
                </span>
              </label>
            ))}
          </div>
          <p className="hint">
            You can change this whenever you like — just tell us.
          </p>
        </Section>
      )}

      {/* ------------------------------------------------ 2c. the author */}
      {/* Session 55. Every post carries a byline and author schema, and the
          byline links to an About page on the client's own site. Twice a draft
          went out with the wrong link because nobody had been asked. */}
      {has('blogs') && (
        <Section
          id="author"
          title="Who the posts are by"
          lede="Every post carries a short author line, and search engines read it too. Usually the owner or the person clients already know."
          saved={sections.author}
          prefilled={prefilled}
          canSave={!!author.name.trim()}
          onSave={() => saveSection('author', author)}
        >
          <div className="inline-fields">
            <label className="field">
              <span className="field-label">Name</span>
              <input type="text" value={author.name} maxLength={120} placeholder="Dr. Jane Doe, DPT"
                     onChange={(e) => setAuthor({ ...author, name: e.target.value })} />
            </label>
            <label className="field">
              <span className="field-label">Title</span>
              <input type="text" value={author.title} maxLength={160} placeholder="Founder, Example Co"
                     onChange={(e) => setAuthor({ ...author, title: e.target.value })} />
            </label>
          </div>
          <label className="field">
            <span className="field-label">One sentence about them</span>
            <textarea rows={2} value={author.bio} maxLength={600}
                      placeholder="Twenty years fixing roofs in Ventura County, and still on site most days."
                      onChange={(e) => setAuthor({ ...author, bio: e.target.value })} />
          </label>
          <label className="field">
            <span className="field-label">The page on your site that is about them (or about you as a business)</span>
            <input type="url" value={author.url} maxLength={400} placeholder="https://yourbusiness.com/about"
                   onChange={(e) => setAuthor({ ...author, url: e.target.value })} />
          </label>
          <p className="hint">This is where the author line links. It should be on your own website.</p>
        </Section>
      )}

      {/* ------------------------------------------------ 2c2. the brand (Session 64) */}
      {/* Asked of everybody, once. The newsletter and the title images both
          read it (Leena: asking twice was a duplicate). */}
      <Section
        id="brand"
        title="Your logo and colours"
        lede="We use these on your newsletter and on the title image at the top of each post. If you are not sure of your colours, the ones on your website are a good start."
        saved={sections.brand}
        prefilled={prefilled}
        optional
        onSave={() => saveSection('brand', brandPayload(brand))}
      >
        <BrandSetup value={brand} onChange={setBrand} clientId={me.client_id} />
      </Section>

      {/* ------------------------------------------------ 2d. the links */}
      {has('blogs') && (
        <Section
          id="links"
          title="Pages we may link to"
          lede="Posts link back to your own pages: the services you sell, your About page, your contact page. These are the ones we have. Untick any a post should not point at, add any we missed."
          saved={sections.links}
          prefilled={prefilled}
          optional
          onSave={() => {
            const extra = links.extra.split(/[\n,\s]+/).map((x) => x.trim()).filter(Boolean);
            return saveSection('links', { allowlist: [...links.allowlist, ...extra] });
          }}
        >
          {links.allowlist.length === 0 && <p className="hint">We have none yet beyond your service pages. Add the paths below.</p>}
          <div className="onb-words">
            {links.allowlist.map((p) => (
              <button key={p} type="button" className="onb-word onb-word-on" aria-pressed="true" title="Remove"
                      onClick={() => setLinks({ ...links, allowlist: links.allowlist.filter((x) => x !== p) })}>
                {p} ×
              </button>
            ))}
          </div>
          {(me.money_pages || []).length > 0 && (
            <p className="hint">Your service pages ({me.money_pages.join(', ')}) are always allowed, because that is where a post sends readers.</p>
          )}
          <label className="field">
            <span className="field-label">Any others?</span>
            <textarea rows={2} value={links.extra} maxLength={2000}
                      placeholder="/faq, /case-studies, /contact — one per line or comma separated"
                      onChange={(e) => setLinks({ ...links, extra: e.target.value })} />
          </label>
        </Section>
      )}

      {/* ------------------------------------------------ 2e. proof */}
      {has('blogs') && (
        <Section
          id="proof"
          title="Proof we can use"
          lede="Things that are true and checkable: years in business, review counts, awards, certifications, named clients. A post with one real proof point in it reads very differently from one without."
          saved={sections.proof}
          prefilled={prefilled}
          optional
          onSave={() => saveSection('proof', { proof })}
        >
          <ProofRows rows={proof} setRows={setProof} />
        </Section>
      )}

      {/* ------------------------------------------------ 2f. images (Session 64) */}
      {has('blogs') && (
        <Section
          id="images"
          title="The picture at the top of each post"
          lede="Every post has one. We can make a title image for you, or you add your own photos."
          saved={sections.images}
          prefilled={prefilled}
          canSave={!!images.source}
          onSave={() => saveSection('images', { source: images.source, look: images.look, notes: images.notes })}
        >
          <ImagesSetup value={images} onChange={setImages} brand={brand} />
        </Section>
      )}

      {/* ------------------------------------------------ 2g. the newsletter */}
      {/* Wave 13 (session 46). Only for a client who bought the newsletter or
          had it switched on; everybody else never sees it (definition of
          done 1). Saved through portal_save_onboarding('newsletter'), which
          promotes it into pack.newsletter, the copy the droplet builds from. */}
      {hasNewsletter && (
        <Section
          id="newsletter"
          title="Your newsletter"
          lede="Every post that goes out becomes a newsletter in your brand, ready to paste into your own email tool and send. Pick how it should look."
          saved={sections.newsletter}
          prefilled={prefilled}
          canSave={newsletterComplete(newsletter)}
          onSave={() => saveSection('newsletter', newsletterPayload(newsletter))}
        >
          <NewsletterSetup
            value={newsletter}
            onChange={setNewsletter}
            business={{ name: me.name, website: biz.website || me.website }}
            clientId={me.client_id}
            brand={brand}   // Session 64: logo, colours and font from the brand step
            // decision 21: footer details asked only if the business pack lacks them
            askFooter={!(me.business?.city && me.business?.phone && me.business?.email)}
          />
          {!newsletterComplete(newsletter) && (
            <p className="hint">Pick a template and give the button some text and a link, and this will save.</p>
          )}
        </Section>
      )}

      {/* ------------------------------------------------- 3. what happens */}
      <section className="review-section onb-next">
        <h2>{returning ? 'Done changing things?' : 'What happens once you are done'}</h2>
        {!returning && nextGroups.map((g) => {
          const next = WHAT_NEXT[g.group_code];
          if (!next) return null;
          return (
            <div key={g.group_code} className="onb-next-block">
              <h3 className="onb-sub">{next.title}</h3>
              <ul>{next.lines.map((l, i) => <li key={i}>{l}</li>)}</ul>
            </div>
          );
        })}
        {returning && <p className="hint">Each section saved on its own as you went. This just tells us you are finished.</p>}
        <div className="review-actions" style={{ justifyContent: 'flex-start' }}>
          <button type="button" className="btn btn-primary"
                  disabled={finishing || !canFinish} onClick={finish}>
            {finishing ? 'Finishing…' : returning ? "That's everything" : "That's everything — I'm done"}
          </button>
        </div>
        {!canFinish && (
          <p className="hint">
            Save {!businessDone ? 'the business section'
                  : !blogsDone ? 'the blog section'
                  : !approvalDone ? 'how much you want to be asked'
                  : !authorDone ? 'who the posts are by'
                  : !imagesDone ? 'where the pictures come from'
                  : 'your newsletter section'} first and this will open up.
          </p>
        )}
      </section>
    </>
  );
}
